import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { firestore } from "../firebase";
import useAuth from "../hooks/useAuth";
import TopBar from "./TopBar";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { FiMapPin, FiPhone, FiGlobe, FiDownload, FiTrash2, FiArrowLeft, FiArrowRight, FiX, FiFileText, FiRefreshCw, FiEye, FiEdit2, FiSave, FiClock, FiRotateCw, FiArrowUp } from "react-icons/fi";
import { generateQuotePdfClient } from "../utils/pdf";
import { openOrDownloadPdf } from "../utils/download";
import "../styles/quotation-system.css";

const STAGES = {
  SHOWN_INTEREST: 'shown_interest',
  QUOTATION_SENT: 'quotation_sent',
  SITE_SURVEY: 'site_survey',
  NOT_RESPONSIVE: 'not_responsive',
  DISQUALIFIED: 'disqualified'
};

const STAGE_LABELS = {
  [STAGES.SHOWN_INTEREST]: 'Shown Interest',
  [STAGES.QUOTATION_SENT]: 'Quotation Sent',
  [STAGES.SITE_SURVEY]: 'Site Survey',
  [STAGES.NOT_RESPONSIVE]: 'Not Responsive',
  [STAGES.DISQUALIFIED]: 'Disqualified'
};

export default function ShortlistedLeads({ viewUserId = null }) {
  React.useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "robots";
    metaTag.content = "noindex";
    document.head.appendChild(metaTag);
    document.body.classList.add('quotation-page');
    return () => {
      document.head.removeChild(metaTag);
      document.body.classList.remove('quotation-page');
    };
  }, []);

  const history = useHistory();
  const { user, profile } = useAuth();
  const [activeStage, setActiveStage] = useState(STAGES.SHOWN_INTEREST);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const [editingNotes, setEditingNotes] = useState(new Map()); // Track which leads are in edit mode
  const [notesValues, setNotesValues] = useState(new Map()); // Track notes values while editing
  const [savingNotes, setSavingNotes] = useState(new Set()); // Track which notes are being saved
  const targetUserId = viewUserId || user?.uid;
  const isAdminView = viewUserId !== null && profile?.role === "admin"; // Only show timestamps to admin viewing other users
  const { modal, showAlert, showConfirm, hideModal } = useModal();

  // Show custom notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Format timestamp for display (date and time)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return null;
    
    // Format: "DD MMM YYYY, HH:MM AM/PM"
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');
    
    return `${day} ${month} ${year}, ${formattedHours}:${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (!targetUserId) return;

    let shortlistedUnsubscribe;
    let disqualifiedUnsubscribe;
    let shortlistedLeads = [];
    let disqualifiedLeads = [];

    const updateLeads = () => {
      // Combine shortlisted and disqualified leads
      const allLeads = [...shortlistedLeads, ...disqualifiedLeads];
      setLeads(Array.isArray(allLeads) ? allLeads : []);
      setLoading(false);
    };

    // Load shortlisted leads
    shortlistedUnsubscribe = firestore
      .collection("shortlistedLeads")
      .where("userId", "==", targetUserId)
      .orderBy("transferredAt", "desc")
      .onSnapshot((snapshot) => {
        shortlistedLeads = snapshot.docs.map(doc => ({
          id: doc.id,
          stage: doc.data().stage || STAGES.SHOWN_INTEREST,
          ...doc.data()
        }));
        updateLeads();
      }, (error) => {
        console.error("Error loading shortlisted leads:", error);
        setLoading(false);
      });

    // Load disqualified leads
    disqualifiedUnsubscribe = firestore
      .collection("disqualifiedLeads")
      .where("userId", "==", targetUserId)
      .onSnapshot((snapshot) => {
        disqualifiedLeads = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            stage: STAGES.DISQUALIFIED,
            transferredAt: data.disqualifiedAt || data.transferredAt || Date.now(),
            ...data
          };
        });
        // Sort by disqualifiedAt descending manually
        disqualifiedLeads.sort((a, b) => (b.disqualifiedAt || 0) - (a.disqualifiedAt || 0));
        updateLeads();
      }, (error) => {
        console.error("Error loading disqualified leads:", error);
        setLoading(false);
      });

    return () => {
      if (shortlistedUnsubscribe) shortlistedUnsubscribe();
      if (disqualifiedUnsubscribe) disqualifiedUnsubscribe();
    };
  }, [targetUserId]);

  // Save notes for a lead
  const saveNotes = async (leadId, notes) => {
    if (!user) return;
    
    setSavingNotes(prev => new Set(prev).add(leadId));
    
    try {
      await firestore.collection("shortlistedLeads").doc(leadId).update({
        notes: notes || "",
        notesUpdatedAt: Date.now()
      });
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId 
            ? { ...lead, notes: notes || "", notesUpdatedAt: Date.now() }
            : lead
        )
      );
      
      setEditingNotes(prev => {
        const newMap = new Map(prev);
        newMap.delete(leadId);
        return newMap;
      });
      
      setNotesValues(prev => {
        const newMap = new Map(prev);
        newMap.delete(leadId);
        return newMap;
      });
      
      showNotification("Notes saved successfully", "success");
    } catch (error) {
      console.error("Error saving notes:", error);
      showNotification("Failed to save notes. Please try again.", "error");
    } finally {
      setSavingNotes(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
    }
  };

  // Start editing notes
  const startEditingNotes = (leadId, currentNotes) => {
    setEditingNotes(prev => new Map(prev).set(leadId, true));
    setNotesValues(prev => new Map(prev).set(leadId, currentNotes || ""));
  };

  // Cancel editing notes
  const cancelEditingNotes = (leadId) => {
    setEditingNotes(prev => {
      const newMap = new Map(prev);
      newMap.delete(leadId);
      return newMap;
    });
    setNotesValues(prev => {
      const newMap = new Map(prev);
      newMap.delete(leadId);
      return newMap;
    });
  };

  // Update notes value while editing
  const updateNotesValue = (leadId, value) => {
    setNotesValues(prev => {
      const newMap = new Map(prev);
      newMap.set(leadId, value);
      return newMap;
    });
  };

  const deleteLead = async (leadId, placeId, isDisqualified = false) => {
    const collectionName = isDisqualified ? "disqualifiedLeads" : "shortlistedLeads";
    const message = isDisqualified 
      ? "Are you sure you want to permanently delete this disqualified lead?"
      : "Are you sure you want to remove this lead from shortlisted?";
    
    showConfirm(
      message,
      async () => {
        try {
          // Optimistically remove from local state immediately
          setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
          
          await firestore.collection(collectionName).doc(leadId).delete();
          showNotification("Lead removed successfully", "success");
        } catch (error) {
          console.error("Error deleting lead:", error);
          // Revert optimistic update on error
          // The Firestore listener will restore the correct state
          showNotification("Failed to delete lead. Please try again.", "error");
        }
      },
      "Remove Lead",
      "warning"
    );
  };

  const handleGenerateQuote = (lead) => {
    history.push("/q/new", {
      leadData: {
        name: lead.name,
        phone: lead.phone,
        address: lead.address
      },
      leadId: lead.id
    });
  };

  const handleViewQuote = async (lead) => {
    if (!lead.quoteId) {
      showNotification("No quote found for this lead.", "error");
      return;
    }
    try {
      const quoteDoc = await firestore.collection("quotes").doc(lead.quoteId).get();
      if (!quoteDoc.exists) {
        showNotification("Quote not found.", "error");
        return;
      }
      const quote = quoteDoc.data();
      const { blob, filename } = await generateQuotePdfClient({ quote });
      openOrDownloadPdf(blob, filename);
    } catch (error) {
      console.error("Error viewing quote:", error);
      showNotification("Failed to generate PDF. Please try again.", "error");
    }
  };

  const handleRegenerateQuote = (lead) => {
    if (!lead.quoteId) {
      showNotification("No existing quote found to regenerate.", "error");
      return;
    }
    history.push("/q/new", {
      leadData: {
        name: lead.name,
        phone: lead.phone,
        address: lead.address
      },
      leadId: lead.id,
      quoteId: lead.quoteId // Pass existing quote ID for regeneration
    });
  };

  const moveLeadsToStage = async (targetStage) => {
    if (selectedLeads.size === 0) {
      showNotification("Please select at least one lead to move", "error");
      return;
    }

    const count = selectedLeads.size;
    const selectedIds = Array.from(selectedLeads);
    
    try {
      const batch = firestore.batch();
      selectedIds.forEach(leadId => {
        const docRef = firestore.collection("shortlistedLeads").doc(leadId);
        batch.update(docRef, {
          stage: targetStage,
          stageUpdatedAt: Date.now()
        });
      });

      await batch.commit();
      
      // Optimistically update local state immediately
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          selectedIds.includes(lead.id) 
            ? { ...lead, stage: targetStage, stageUpdatedAt: Date.now() }
            : lead
        )
      );
      
      setSelectedLeads(new Set());
      showNotification(`Successfully moved ${count} lead(s) to ${STAGE_LABELS[targetStage]}`, "success");
    } catch (error) {
      console.error("Error moving leads:", error);
      showNotification("Failed to move leads. Please try again.", "error");
    }
  };

  const moveLeadsToNextStage = async () => {
    if (selectedLeads.size === 0) {
      showNotification("Please select at least one lead to move", "error");
      return;
    }

    const nextStage = getNextStage(activeStage);
    if (!nextStage) {
      showNotification("No next stage available for this stage", "error");
      return;
    }

    await moveLeadsToStage(nextStage);
  };

  const moveLeadsToNotResponsive = async () => {
    await moveLeadsToStage(STAGES.NOT_RESPONSIVE);
  };

  // Restore disqualified lead back to find leads (remove from disqualified)
  const restoreDisqualifiedLead = async (leadId, placeId) => {
    showConfirm(
      "Are you sure you want to restore this lead? It will be removed from disqualified and can appear in search results again.",
      async () => {
        try {
          await firestore.collection("disqualifiedLeads").doc(leadId).delete();
          showNotification("Lead restored successfully", "success");
        } catch (error) {
          console.error("Error restoring lead:", error);
          showNotification("Failed to restore lead. Please try again.", "error");
        }
      },
      "Restore Lead",
      "info"
    );
  };

  // Move disqualified lead to a specific stage in shortlisted
  const moveDisqualifiedToStage = async (leadId, targetStage) => {
    try {
      // Get the lead data from disqualifiedLeads
      const disqualifiedDoc = await firestore.collection("disqualifiedLeads").doc(leadId).get();
      if (!disqualifiedDoc.exists) {
        showNotification("Lead not found", "error");
        return;
      }

      const leadData = disqualifiedDoc.data();

      // Create in shortlistedLeads with the target stage
      await firestore.collection("shortlistedLeads").add({
        userId: leadData.userId,
        placeId: leadData.placeId,
        name: leadData.name,
        phone: leadData.phone,
        address: leadData.address || null,
        website: leadData.website || null,
        coordinates: leadData.coordinates || null,
        rating: leadData.rating || null,
        userRatingsTotal: leadData.userRatingsTotal || 0,
        businessStatus: leadData.businessStatus || null,
        types: leadData.types || [],
        priority: leadData.priority || 0,
        stage: targetStage,
        transferredAt: Date.now(),
        notes: leadData.notes || "",
        notesUpdatedAt: leadData.notesUpdatedAt || null
      });

      // Delete from disqualifiedLeads
      await firestore.collection("disqualifiedLeads").doc(leadId).delete();
      
      showNotification(`Lead moved to ${STAGE_LABELS[targetStage]} successfully`, "success");
    } catch (error) {
      console.error("Error moving disqualified lead:", error);
      showNotification("Failed to move lead. Please try again.", "error");
    }
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const stageLeads = getStageLeads(activeStage);
    if (selectedLeads.size === stageLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(stageLeads.map(l => l.id)));
    }
  };

  const getStageLeads = (stage) => {
    if (!leads || !Array.isArray(leads)) return [];
    return leads.filter(lead => lead && lead.stage === stage);
  };

  const getNextStage = (currentStage) => {
    switch (currentStage) {
      case STAGES.SHOWN_INTEREST:
        return STAGES.QUOTATION_SENT;
      case STAGES.QUOTATION_SENT:
        return STAGES.SITE_SURVEY;
      case STAGES.SITE_SURVEY:
        return null; // No next stage
      case STAGES.NOT_RESPONSIVE:
        return null; // No next stage
      default:
        return null;
    }
  };

  const downloadCSV = () => {
    if (!leads || !Array.isArray(leads) || leads.length === 0) {
      showAlert("No leads to download", "info", "No Leads");
      return;
    }

    const headers = ['Name', 'Phone', 'Website', 'Address', 'Coordinates', 'Rating', 'Business Status', 'Notes'];
    const rows = (Array.isArray(leads) ? leads : []).map(lead => [
      lead.name,
      lead.phone,
      lead.website,
      lead.address,
      lead.coordinates,
      lead.rating || 'N/A',
      lead.businessStatus || 'N/A',
      lead.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `shortlisted_leads_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div className="quotation-system">
      {!viewUserId && <TopBar />}
      <Modal {...modal} onClose={hideModal} />
      {/* Custom Notification */}
      {notification && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: notification.type === 'success' 
              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" 
              : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            animation: "notificationFadeIn 0.3s ease-out",
            maxWidth: "400px",
            minWidth: "300px"
          }}
        >
          <div style={{ fontSize: "1.25rem", flexShrink: 0 }}>
            {notification.type === 'success' ? '✓' : '✕'}
          </div>
          <div style={{ fontWeight: "500" }}>{notification.message}</div>
        </div>
      )}
      <div className="elegant-container fade-in-up">
        <div className="elegant-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem", flexWrap: "wrap", gap: "1rem" }}>
            <div>
              {!viewUserId && (
                <button
                  onClick={() => history.push("/q/leads")}
                  className="btn-elegant btn-elegant-secondary"
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "0.5rem",
                    marginBottom: "1rem"
                  }}
                >
                  <FiArrowLeft size={18} />
                  Back to Find Leads
                </button>
              )}
              <h1 className="elegant-title" style={{ marginBottom: "0.5rem" }}>Shortlisted Leads</h1>
              <p className="elegant-subtitle" style={{ marginBottom: 0 }}>
                {loading ? "Loading..." : `${(leads && Array.isArray(leads) ? leads.length : 0)} lead${(leads && Array.isArray(leads) && leads.length !== 1) ? 's' : ''} shortlisted`}
              </p>
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
              {selectedLeads.size > 0 && activeStage === STAGES.NOT_RESPONSIVE && activeStage !== STAGES.DISQUALIFIED && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <label style={{ fontSize: "0.875rem", color: "var(--neutral-700)", fontWeight: "500" }}>
                    Send to:
                  </label>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        moveLeadsToStage(e.target.value);
                        e.target.value = "";
                      }
                    }}
                    style={{
                      padding: "0.5rem 1rem",
                      borderRadius: "8px",
                      border: "2px solid var(--accent-purple)",
                      background: "white",
                      color: "var(--primary-deep)",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      fontWeight: "500",
                      minWidth: "180px"
                    }}
                    defaultValue=""
                  >
                    <option value="">Select stage...</option>
                    {Object.values(STAGES)
                      .filter(stage => stage !== STAGES.NOT_RESPONSIVE && stage !== STAGES.DISQUALIFIED)
                      .map(stage => (
                        <option key={stage} value={stage}>
                          {STAGE_LABELS[stage]}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              {selectedLeads.size > 0 && getNextStage(activeStage) && activeStage !== STAGES.NOT_RESPONSIVE && activeStage !== STAGES.DISQUALIFIED && (
                <button
                  onClick={moveLeadsToNextStage}
                  className="btn-elegant btn-elegant-accent"
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                >
                  <FiArrowRight size={18} />
                  Send to Next Stage ({selectedLeads.size})
                </button>
              )}
              {selectedLeads.size > 0 && activeStage === STAGES.QUOTATION_SENT && activeStage !== STAGES.DISQUALIFIED && (
                <button
                  onClick={moveLeadsToNotResponsive}
                  className="btn-elegant"
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "0.5rem",
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    color: "white",
                    border: "none"
                  }}
                >
                  <FiX size={18} />
                  Mark as Not Responsive ({selectedLeads.size})
                </button>
              )}
              {!isMobile && leads && Array.isArray(leads) && leads.length > 0 && (
                <button
                  onClick={downloadCSV}
                  className="btn-elegant btn-elegant-secondary"
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                >
                  <FiDownload size={18} />
                  Download CSV
                </button>
              )}
            </div>
          </div>

          {/* Stage Tabs */}
          {!loading && leads && Array.isArray(leads) && leads.length > 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  marginBottom: "1rem",
                  borderBottom: "2px solid var(--neutral-200)",
                  paddingBottom: "0.5rem",
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none"
                }}
                className="stage-tabs-container"
              >
                {Object.values(STAGES).map((stage) => {
                  const stageLeads = getStageLeads(stage);
                  return (
                    <button
                      key={stage}
                      onClick={() => {
                        setActiveStage(stage);
                        setSelectedLeads(new Set()); // Clear selection when switching tabs
                      }}
                      className="btn-elegant"
                      style={{
                        background: activeStage === stage ? "var(--gradient-primary)" : "transparent",
                        color: activeStage === stage ? "white" : "var(--neutral-700)",
                        border: activeStage === stage ? "none" : "2px solid var(--neutral-300)",
                        borderRadius: "8px 8px 0 0",
                        padding: "0.75rem 1rem",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        transition: "all 0.3s",
                        marginBottom: "-2px",
                        borderBottom: activeStage === stage ? "2px solid transparent" : "2px solid var(--neutral-200)",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        fontSize: "0.875rem"
                      }}
                    >
                      {STAGE_LABELS[stage]} ({stageLeads.length})
                    </button>
                  );
                })}
              </div>
              
              {/* Select All Checkbox */}
              {getStageLeads(activeStage).length > 0 && (
                <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={selectedLeads.size === getStageLeads(activeStage).length && getStageLeads(activeStage).length > 0}
                      onChange={toggleSelectAll}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    <span style={{ fontSize: "0.875rem", color: "var(--neutral-600)", fontWeight: "500" }}>
                      Select All ({selectedLeads.size} selected)
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
              <span className="loading-elegant"></span>
            </div>
          ) : (!leads || !Array.isArray(leads) || leads.length === 0) ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "var(--neutral-600)" }}>
              <p>No shortlisted leads yet. Transfer leads from the Find Leads page to get started.</p>
            </div>
          ) : getStageLeads(activeStage).length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", color: "var(--neutral-600)" }}>
              <p>No leads in {STAGE_LABELS[activeStage]} stage.</p>
            </div>
          ) : (
            <>
              <div
                className="leads-grid-container"
                style={{
                  display: "grid",
                  gap: "1rem",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(400px, 1fr))"
                }}
              >
                {getStageLeads(activeStage).map((lead) => {
                  return (
                  <div
                    key={lead.id}
                    style={{
                      background: "white",
                      border: selectedLeads.has(lead.id) ? "2px solid var(--accent-purple)" : "2px solid var(--neutral-200)",
                      borderRadius: "12px",
                      padding: "1.5rem",
                      transition: "all 0.3s",
                      boxShadow: selectedLeads.has(lead.id) ? "0 4px 20px rgba(139, 92, 246, 0.2)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
                      position: "relative"
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedLeads.has(lead.id)) {
                        e.currentTarget.style.borderColor = "var(--accent-purple)";
                        e.currentTarget.style.boxShadow = "0 4px 20px rgba(139, 92, 246, 0.15)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedLeads.has(lead.id)) {
                        e.currentTarget.style.borderColor = "var(--neutral-200)";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    <div style={{ position: "absolute", top: "1rem", right: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => toggleLeadSelection(lead.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer"
                        }}
                      />
                      <button
                        onClick={() => deleteLead(lead.id, lead.placeId, lead.stage === STAGES.DISQUALIFIED)}
                        style={{
                          background: "rgba(239, 68, 68, 0.1)",
                          border: "1px solid var(--error)",
                          borderRadius: "8px",
                          padding: "0.5rem",
                          cursor: "pointer",
                          color: "var(--error)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        title={lead.stage === STAGES.DISQUALIFIED ? "Permanently delete this disqualified lead" : "Remove from shortlisted"}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", marginBottom: "1rem" }}>
                      <input
                        type="checkbox"
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => toggleLeadSelection(lead.id)}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          marginTop: "0.25rem",
                          flexShrink: 0
                        }}
                      />
                      <h4
                        style={{
                          fontSize: "1.125rem",
                          fontWeight: "600",
                          color: "var(--primary-deep)",
                          lineHeight: "1.4",
                          flex: 1,
                          margin: 0,
                          paddingRight: "3rem"
                        }}
                      >
                        {lead.name}
                      </h4>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      {lead.phone !== 'Not available' && (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                          <FiPhone size={18} style={{ color: "var(--accent-purple)", marginTop: "0.125rem", flexShrink: 0 }} />
                          <a
                            href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`}
                            style={{
                              color: "var(--accent-purple)",
                              textDecoration: "none",
                              fontWeight: "500",
                              wordBreak: "break-all"
                            }}
                          >
                            {lead.phone}
                          </a>
                        </div>
                      )}

                      {lead.website !== 'Not available' && (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                          <FiGlobe size={18} style={{ color: "var(--accent-cyan)", marginTop: "0.125rem", flexShrink: 0 }} />
                          <a
                            href={lead.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "var(--accent-cyan)",
                              textDecoration: "none",
                              wordBreak: "break-all"
                            }}
                          >
                            {lead.website && lead.website.length > 50 ? `${lead.website.substring(0, 50)}...` : (lead.website || 'Not available')}
                          </a>
                        </div>
                      )}

                      {lead.address && (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                          <FiMapPin size={18} style={{ color: "var(--accent-pink)", marginTop: "0.125rem", flexShrink: 0 }} />
                          <span style={{ color: "var(--neutral-700)", fontSize: "0.9375rem", lineHeight: "1.5" }}>
                            {lead.address}
                          </span>
                        </div>
                      )}

                      {lead.coordinates !== 'Not available' && (
                        <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)", fontFamily: "monospace" }}>
                          📍 {lead.coordinates}
                        </div>
                      )}

                      {lead.rating && (
                        <div style={{ fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                          ⭐ {lead.rating} ({lead.userRatingsTotal || 0} reviews)
                        </div>
                      )}

                      {lead.priority >= 8 && (
                        <span
                          style={{
                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            color: "white",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "12px",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "inline-block",
                            width: "fit-content"
                          }}
                        >
                          High Priority
                        </span>
                      )}
                      
                      <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "var(--neutral-600)" }}>
                        Stage: <strong>{STAGE_LABELS[lead.stage]}</strong>
                      </div>

                      {/* Notes Section */}
                      <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--neutral-200)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                          <label style={{ fontSize: "0.875rem", fontWeight: "600", color: "var(--neutral-700)" }}>
                            Notes
                          </label>
                          {!editingNotes.has(lead.id) && (
                            <button
                              onClick={() => startEditingNotes(lead.id, lead.notes)}
                              style={{
                                background: "rgba(139, 92, 246, 0.1)",
                                border: "1px solid var(--accent-purple)",
                                borderRadius: "6px",
                                padding: "0.375rem 0.75rem",
                                cursor: "pointer",
                                color: "var(--accent-purple)",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.375rem",
                                fontSize: "0.8125rem",
                                fontWeight: "500",
                                transition: "all 0.2s"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)";
                              }}
                            >
                              <FiEdit2 size={14} />
                              {lead.notes ? "Edit" : "Add Note"}
                            </button>
                          )}
                        </div>
                        
                        {editingNotes.has(lead.id) ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <textarea
                              value={notesValues.get(lead.id) || ""}
                              onChange={(e) => updateNotesValue(lead.id, e.target.value)}
                              placeholder="Add notes about this lead..."
                              style={{
                                width: "100%",
                                minHeight: "80px",
                                padding: "0.75rem",
                                borderRadius: "8px",
                                border: "2px solid var(--accent-purple)",
                                fontSize: "0.875rem",
                                fontFamily: "inherit",
                                resize: "vertical",
                                color: "var(--neutral-700)",
                                background: "white",
                                lineHeight: "1.5"
                              }}
                              disabled={savingNotes.has(lead.id)}
                            />
                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                              <button
                                onClick={() => cancelEditingNotes(lead.id)}
                                disabled={savingNotes.has(lead.id)}
                                style={{
                                  padding: "0.5rem 1rem",
                                  borderRadius: "6px",
                                  border: "1px solid var(--neutral-300)",
                                  background: "white",
                                  color: "var(--neutral-700)",
                                  cursor: savingNotes.has(lead.id) ? "not-allowed" : "pointer",
                                  fontSize: "0.8125rem",
                                  fontWeight: "500",
                                  opacity: savingNotes.has(lead.id) ? 0.5 : 1
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => saveNotes(lead.id, notesValues.get(lead.id) || "")}
                                disabled={savingNotes.has(lead.id)}
                                className="btn-elegant btn-elegant-accent"
                                style={{
                                  padding: "0.5rem 1rem",
                                  fontSize: "0.8125rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.375rem",
                                  opacity: savingNotes.has(lead.id) ? 0.7 : 1,
                                  cursor: savingNotes.has(lead.id) ? "not-allowed" : "pointer"
                                }}
                              >
                                {savingNotes.has(lead.id) ? (
                                  <>
                                    <span className="loading-elegant" style={{ width: "14px", height: "14px", borderTopColor: "white" }}></span>
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <FiSave size={14} />
                                    Save
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              padding: "0.75rem",
                              borderRadius: "8px",
                              background: lead.notes ? "rgba(139, 92, 246, 0.05)" : "rgba(0, 0, 0, 0.02)",
                              border: lead.notes ? "1px solid rgba(139, 92, 246, 0.2)" : "1px solid var(--neutral-200)",
                              minHeight: "60px",
                              fontSize: "0.875rem",
                              color: lead.notes ? "var(--neutral-700)" : "var(--neutral-500)",
                              lineHeight: "1.5",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word"
                            }}
                          >
                            {lead.notes || "No notes added yet. Click 'Add Note' to add notes about this lead."}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons based on stage */}
                      <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        {activeStage === STAGES.SHOWN_INTEREST && (
                          <button
                            onClick={() => handleGenerateQuote(lead)}
                            className="btn-elegant btn-elegant-accent"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              fontSize: "0.875rem",
                              padding: "0.5rem 1rem"
                            }}
                          >
                            <FiFileText size={16} />
                            Generate Quote
                          </button>
                        )}
                        {(activeStage === STAGES.QUOTATION_SENT || activeStage === STAGES.SITE_SURVEY) && lead.quoteId && (
                          <>
                            <button
                              onClick={() => handleViewQuote(lead)}
                              className="btn-elegant btn-elegant-secondary"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                fontSize: "0.875rem",
                                padding: "0.5rem 1rem"
                              }}
                            >
                              <FiEye size={16} />
                              View Quote
                            </button>
                            {activeStage === STAGES.SITE_SURVEY && (
                              <button
                                onClick={() => handleRegenerateQuote(lead)}
                                className="btn-elegant"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.5rem",
                                  fontSize: "0.875rem",
                                  padding: "0.5rem 1rem",
                                  background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                  color: "white",
                                  border: "none"
                                }}
                              >
                                <FiRefreshCw size={16} />
                                Re-generate Quote
                              </button>
                            )}
                          </>
                        )}
                        {activeStage === STAGES.DISQUALIFIED && (
                          <>
                            <button
                              onClick={() => restoreDisqualifiedLead(lead.id, lead.placeId)}
                              className="btn-elegant btn-elegant-secondary"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                fontSize: "0.875rem",
                                padding: "0.5rem 1rem"
                              }}
                            >
                              <FiRotateCw size={16} />
                              Restore to Find Leads
                            </button>
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  moveDisqualifiedToStage(lead.id, e.target.value);
                                  e.target.value = "";
                                }
                              }}
                              style={{
                                padding: "0.5rem 1rem",
                                borderRadius: "8px",
                                border: "2px solid var(--accent-purple)",
                                background: "white",
                                color: "var(--primary-deep)",
                                fontSize: "0.875rem",
                                cursor: "pointer",
                                fontWeight: "500",
                                minWidth: "180px"
                              }}
                              defaultValue=""
                            >
                              <option value="">Move to stage...</option>
                              {Object.values(STAGES)
                                .filter(stage => stage !== STAGES.DISQUALIFIED)
                                .map(stage => (
                                  <option key={stage} value={stage}>
                                    {STAGE_LABELS[stage]}
                                  </option>
                                ))}
                            </select>
                          </>
                        )}
                      </div>
                    </div>

                    {isMobile && (
                      <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                        {lead.phone !== 'Not available' && (
                          <a
                            href={`tel:${lead.phone.replace(/[^\d+]/g, '')}`}
                            className="btn-elegant btn-elegant-accent"
                            style={{
                              flex: 1,
                              textAlign: "center",
                              textDecoration: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.5rem"
                            }}
                          >
                            <FiPhone size={18} />
                            Call
                          </a>
                        )}
                        {lead.website !== 'Not available' && (
                          <a
                            href={lead.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-elegant btn-elegant-secondary"
                            style={{
                              flex: 1,
                              textAlign: "center",
                              textDecoration: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.5rem"
                            }}
                          >
                            <FiGlobe size={18} />
                            Visit
                          </a>
                        )}
                      </div>
                    )}

                    {/* Admin-only Timestamps Section - At bottom of lead card */}
                    {isAdminView && (
                      <div style={{ 
                        marginTop: "1rem", 
                        paddingTop: "1rem", 
                        borderTop: "1px solid var(--neutral-200)",
                        background: "rgba(139, 92, 246, 0.03)",
                        borderRadius: "6px",
                        padding: "0.75rem"
                      }}>
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "0.5rem", 
                          marginBottom: "0.5rem" 
                        }}>
                          <FiClock size={14} style={{ color: "var(--accent-purple)" }} />
                          <span style={{ 
                            fontSize: "0.8125rem", 
                            fontWeight: "600", 
                            color: "var(--neutral-700)" 
                          }}>
                            Activity Timeline (Admin View)
                          </span>
                        </div>
                        <div style={{ 
                          display: "flex", 
                          flexDirection: "column", 
                          gap: "0.5rem",
                          fontSize: "0.8125rem",
                          color: "var(--neutral-600)"
                        }}>
                          {lead.transferredAt && (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                              <span style={{ color: "var(--accent-purple)", fontWeight: "500", minWidth: "140px" }}>
                                Shortlisted:
                              </span>
                              <span>{formatTimestamp(lead.transferredAt)}</span>
                            </div>
                          )}
                          {lead.quoteGeneratedAt && (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                              <span style={{ color: "var(--accent-cyan)", fontWeight: "500", minWidth: "140px" }}>
                                Quote Generated:
                              </span>
                              <span>{formatTimestamp(lead.quoteGeneratedAt)}</span>
                            </div>
                          )}
                          {lead.stageUpdatedAt && lead.stage !== STAGES.SHOWN_INTEREST && (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                              <span style={{ color: "var(--accent-pink)", fontWeight: "500", minWidth: "140px" }}>
                                Moved to {STAGE_LABELS[lead.stage]}:
                              </span>
                              <span>{formatTimestamp(lead.stageUpdatedAt)}</span>
                            </div>
                          )}
                          {lead.notesUpdatedAt && (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                              <span style={{ color: "var(--neutral-600)", fontWeight: "500", minWidth: "140px" }}>
                                Notes Updated:
                              </span>
                              <span>{formatTimestamp(lead.notesUpdatedAt)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              {isMobile && (
                <div style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {selectedLeads.size > 0 && activeStage === STAGES.NOT_RESPONSIVE && (
                    <div>
                      <label style={{ display: "block", fontSize: "0.875rem", color: "var(--neutral-700)", fontWeight: "500", marginBottom: "0.5rem" }}>
                        Send to:
                      </label>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            moveLeadsToStage(e.target.value);
                            e.target.value = "";
                          }
                        }}
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          borderRadius: "8px",
                          border: "2px solid var(--accent-purple)",
                          background: "white",
                          color: "var(--primary-deep)",
                          fontSize: "0.875rem",
                          cursor: "pointer",
                          fontWeight: "500"
                        }}
                        defaultValue=""
                      >
                        <option value="">Select stage...</option>
                        {Object.values(STAGES)
                          .filter(stage => stage !== STAGES.NOT_RESPONSIVE)
                          .map(stage => (
                            <option key={stage} value={stage}>
                              {STAGE_LABELS[stage]}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  {selectedLeads.size > 0 && getNextStage(activeStage) && activeStage !== STAGES.NOT_RESPONSIVE && (
                    <button
                      onClick={moveLeadsToNextStage}
                      className="btn-elegant btn-elegant-accent"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem"
                      }}
                    >
                      <FiArrowRight size={18} />
                      Send to Next Stage ({selectedLeads.size})
                    </button>
                  )}
                  {selectedLeads.size > 0 && activeStage === STAGES.QUOTATION_SENT && activeStage !== STAGES.DISQUALIFIED && (
                    <button
                      onClick={moveLeadsToNotResponsive}
                      className="btn-elegant"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                        color: "white",
                        border: "none"
                      }}
                    >
                      <FiX size={18} />
                      Mark as Not Responsive ({selectedLeads.size})
                    </button>
                  )}
                  {leads && Array.isArray(leads) && leads.length > 0 && (
                    <button
                      onClick={downloadCSV}
                      className="btn-elegant btn-elegant-secondary"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem"
                      }}
                    >
                      <FiDownload size={18} />
                      Download CSV
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

